import * as store from '@/store';
import {OwnerType, InheritRepo, GroupType} from '@/Constant';
import {
    AuthType,
    AuthItem,
    AuthFnParams,
    ErrorItem,
    ROLE,
    DOC_SCOPE_TYPE,
    BOOK_TYPE,
    REPO_SCOPE_TYPE,
} from './auth.td';

export interface AuthObjType {
    auth?: AuthItem | AuthItem[];
    error?: ErrorItem | ErrorItem[];
    condition?: 'or' | 'and';
    fn?: (fnParams: AuthFnParams, sourceData?: any, ...args: any) => any;
    byOtherMaps?: Array<{ type: AuthType, mapName: string }>;
    authOrigin?: any;
    errorOrigin?: any;
}

const AuthDocument = {
    authValue: {authOrigin: {type: 'document', keyName: 'roleName'}},
    errorValue: {errorOrigin: {type: 'document'}},
    error: {error: {type: 'document'}},
    notFound: {
        error: [
            {type: 'document', checkCode: 404},
            {type: 'repository', checkCode: 404},
        ],
        condition: 'or',
    },
    noPermission: {
        error: [
            {type: 'document', checkCode: 403},
            {type: 'repository', checkCode: 403},
        ],
        condition: 'or',
    },
    isReader: {
        auth: [
            {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocReader},
            {type: 'document', keyName: 'scope', requestAuth: DOC_SCOPE_TYPE.publicRead},
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Reader},
            {type: 'repository', keyName: 'scope', requestAuth: REPO_SCOPE_TYPE.public},
        ],
        condition: 'or',
    },
    isMember: { // 能不能编辑
        auth: [
            {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocMember},
            {type: 'document', keyName: 'scope', requestAuth: DOC_SCOPE_TYPE.publicEdit},
            {type: 'document', keyName: 'bookType', requestAuth: BOOK_TYPE.IS_BOOK},
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Member},
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Admin},
        ],
        fn: ({authValues, originValues}: AuthFnParams) => {
            const [roleName] = originValues || [];
            if (!roleName) {
                return false;
            }
            const [isDocMember, isDocPublicEdit, isBook, isRepoMember, isRepoAdmin] = authValues || [];
            if (isBook) {
                return isDocMember || isDocPublicEdit || isRepoAdmin;
            }
            return isDocMember || isDocPublicEdit || isRepoMember;
        },
    },
    isAdmin: {
        auth: [
            {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocAdmin},
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Admin},
        ],
        condition: 'or',
    },
    isReaderEqual: {auth: {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocReader, strictEqual: true}},
    isMemberEqual: {auth: {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocMember, strictEqual: true}},
    isAdminEqual: {auth: {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocAdmin, strictEqual: true}},
    isPublic: {
        auth: {type: 'document', keyName: 'scope', requestAuth: DOC_SCOPE_TYPE.publicRead}, // publicRead 以上权限均可
    },
    isNotPublic: {
        auth: {type: 'document', keyName: 'scope', requestAuth: DOC_SCOPE_TYPE.private, strictEqual: true},
    },
    isPublicRead: {
        auth: {type: 'document', keyName: 'scope', requestAuth: DOC_SCOPE_TYPE.publicRead},
    },
    isPublicEdit: {
        auth: {type: 'document', keyName: 'scope', requestAuth: DOC_SCOPE_TYPE.publicEdit},
    },
    isAdminPage: {
        auth: {type: 'document', keyName: 'repositoryRoleName', requestAuth: ROLE.Admin},
    },
    isAdminByDoc: {
        auth: {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocAdmin, strictEqual: true},
        fn: ({authValue}: AuthFnParams, document?: any) => {
            if (document !== undefined) {
                return document.docRoleName === ROLE.DocAdmin;
            }
            return authValue;
        },
    },
    isMemberByRoleName: {
        auth: {type: 'document', keyName: 'roleName', requestAuth: ROLE.DocAdmin, strictEqual: true},
        fn: ({authValue}: AuthFnParams, docRoleName?: string) => {
            if (docRoleName !== undefined) {
                return docRoleName === ROLE.Member;
            }
            return authValue;
        },
    },
    canEditByDoc: {
        byOtherMaps: [{type: 'repository', name: 'canEditInKnowledge'}],
        fn: ({otherMapAuths}: AuthFnParams, document?: WizDocument) => {
            const [canEditRepository] = otherMapAuths || [];
            if (document === undefined) {
                document = store.getData(store.KEYS.CUR_DOCUMENT);
            }
            const canEditDoc = document?.docRoleName === ROLE.Member; // 文档编辑权限
            return canEditRepository || canEditDoc;
        },
    },
    canInvite: {
        auth: {type: 'document', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => {
            return permissions?.includes('mdr') || permissions?.includes('mde') || permissions?.includes('mda');
        },
    },
    canInviteReader: { // 当前用户能否在当前文档邀请只读者
        auth: {type: 'document', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => permissions?.includes('mdr'),
    },
    canInviteEditor: { // 当前用户能否在当前文档邀请编辑者
        auth: {type: 'document', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => permissions?.includes('mde'),
    },
    canInviteAdmin: { // 当前用户能否在当前文档邀请管理员
        auth: {type: 'document', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => permissions?.includes('mda'),
    },
};

const AuthRepository = {
    authValue: {authOrigin: {type: 'repository', keyName: 'roleName'}},
    errorValue: {errorOrigin: {type: 'repository'}},
    error: {error: {type: 'repository'}},
    notFound: {error: {type: 'repository', checkCode: 404}},
    noPermission: {error: {type: 'repository', checkCode: 403}},
    isReader: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Reader}},
    isMember: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Member}},
    isAdmin: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Admin}},
    isAAOwner: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.AAOwner}},
    isReaderEqual: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Reader, strictEqual: true}},
    isMemberEqual: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Member, strictEqual: true}},
    isAdminEqual: {auth: {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Admin, strictEqual: true}},
    isPersonal: {
        auth: {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User},
    },
    isPersonalMyself: {
        auth: {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User},
        fn: ({authValue}: AuthFnParams, repository?: Repository) => {
            const isPersonal = authValue;
            if (repository === undefined) {
                repository = store.getData(store.KEYS.CUR_REPOSITORY);
            }
            const userGuid = store.getData(store.KEYS.USER)?.userGuid;
            return isPersonal && repository?.userGuid === userGuid;
        },
    },
    isTeam: {
        auth: {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User, negative: true},
    },
    isTeamOwner: {
        auth: {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User, negative: true},
        fn: ({authValue}: AuthFnParams, repository?: Repository) => {
            const isTeam = authValue;
            if (repository === undefined) {
                repository = store.getData(store.KEYS.CUR_REPOSITORY);
            }
            const userGuid = store.getData(store.KEYS.USER)?.userGuid;
            return isTeam && repository?.userGuid === userGuid;
        },
    },
    isInDepartment: {
        auth: {type: 'repository', keyName: 'groupGuid'},
        fn: ({originValue: groupGuid}: AuthFnParams) => {
            return groupGuid !== 'pKzJfZczuc';
        },
    },
    canSettingKnowledge: {
        auth: [
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Admin},
            {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User},
        ],
        fn: ({authValues}: AuthFnParams, repository?: Repository) => {
            const [isAdmin, isPersonalRepo] = authValues || [];
            if (repository === undefined) {
                repository = store.getData(store.KEYS.CUR_REPOSITORY);
            }
            const userGuid = store.getData(store.KEYS.USER)?.userGuid;
            return isAdmin || (isPersonalRepo && repository?.userGuid === userGuid);
        },
    },
    canReadInKnowledge: {
        auth: [
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Reader},
            {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User},
        ],
        fn: ({authValues}: AuthFnParams, repository?: Repository) => {
            const [isReader, isPersonalRepo] = authValues || [];
            if (repository === undefined) {
                repository = store.getData(store.KEYS.CUR_REPOSITORY);
            }
            const userGuid = store.getData(store.KEYS.USER)?.userGuid;
            return isReader || (isPersonalRepo && repository?.userGuid === userGuid);
        },
    },
    canEditInKnowledge: {
        auth: [
            {type: 'repository', keyName: 'roleName', requestAuth: ROLE.Member},
            {type: 'repository', keyName: 'ownerType', requestAuth: OwnerType.User},
        ],
        fn: ({authValues}: AuthFnParams, repository?: Repository) => {
            const [isMember, isPersonalRepo] = authValues || [];
            if (repository === undefined) {
                repository = store.getData(store.KEYS.CUR_REPOSITORY);
            }
            const userGuid = store.getData(store.KEYS.USER)?.userGuid;
            return isMember || (isPersonalRepo && repository?.userGuid === userGuid);
        },
    },
    canInvite: {
        auth: {type: 'repository', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => {
            return permissions?.includes('mrr') || permissions?.includes('mre') || permissions?.includes('mra');
        },
    },
    canInviteReader: {
        auth: {type: 'repository', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => permissions?.includes('mrr'),
    },
    canInviteMember: {
        auth: {type: 'repository', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => permissions?.includes('mre'),
    },
    canInviteAdmin: {
        auth: {type: 'repository', keyName: 'permissions'},
        fn: ({originValue: permissions}: AuthFnParams) => permissions?.includes('mra'),
    },
};

const AuthDepartment = {
    authValue: {authOrigin: {type: 'department', keyName: 'roleName'}},
    errorValue: {errorOrigin: {type: 'department'}},
    error: {error: {type: 'department'}},
    notFound: {error: {type: 'department', checkCode: 404}},
    noPermission: {error: {type: 'department', checkCode: 403}},
    isMember: {auth: {type: 'department', keyName: 'roleName', requestAuth: ROLE.Member}},
    isAdmin: {auth: {type: 'department', keyName: 'roleName', requestAuth: ROLE.Admin}},
    isAAOwner: {auth: {type: 'department', keyName: 'roleName', requestAuth: ROLE.AAOwner}},
    isMemberEqual: {auth: {type: 'department', keyName: 'roleName', requestAuth: ROLE.Member, strictEqual: true}},
    isAdminEqual: {auth: {type: 'department', keyName: 'roleName', requestAuth: ROLE.Admin, strictEqual: true}},
    isAAOwnerEqual: {auth: {type: 'department', keyName: 'roleName', requestAuth: ROLE.AAOwner, strictEqual: true}},
    isVisitor: {
        auth: {type: 'department', keyName: 'roleName', requestAuth: null, strictEqual: true},
    },
    isGUGroup: {
        auth: {type: 'department', keyName: 'groupType', requestAuth: GroupType.GUGroup},
    },
};

const KuAuthMap = {
    document: AuthDocument,
    repository: AuthRepository,
    department: AuthDepartment,
};

export type AuthMapType = typeof KuAuthMap;
export type AuthDocumentKey = keyof typeof AuthDocument;
export type AuthRepositoryKey = keyof typeof AuthRepository;
export type AuthDepartmentKey = keyof typeof AuthDepartment;
export type AuthDocumentResult = {
    [K in AuthDocumentKey]?: any
};
export type AuthRepositoryResult = {
    [K in AuthRepositoryKey]?: any
};
export type AuthDepartmentResult = {
    [K in AuthDepartmentKey]?: any
};

export type AuthResultType = AuthDocumentResult & AuthRepositoryResult & AuthDepartmentResult;

export default KuAuthMap;
